import { useState } from "react";
import Loading from "../../components/UI/Loading/Loading";
import { statisticsService } from "../../services/api";
import FiltersSection from "../../components/Filter/Filter";
import { StatCardPercentage } from "../../components/StatCard/StatCardPercentage";
import LineChart from "../../components/Charts/LineChart";
import "./index.css";
import { fmtCurrencyBR, fmtNumberBR } from "../../utils/formatBrl";
import { SkeletonCard, SkeletonChart } from "../../components/SkeletonLoading/SkeletonLoading";
import { StatCardStatistics } from "../../components/StatCard/StatCardStatistics";
import { RankingItem } from "../../components/StatCard/RankingItem";
import { useAuth } from "../../context/AuthProvider.jsx";
import { getVisibleBots } from "../../utils/bots";
import CountriesSection from "./Countries/CountriesSection";
import {
  LineChart as LineChartIcon, BarChart3, Users, Tags, Crown, Calendar, Clock,
  Banknote, Hourglass, CheckCircle, RefreshCw, PieChart, ArrowRightLeft, Repeat,
  DollarSign, PauseCircle, Ban, Barcode, Box, ArrowDown, Gift, Send, ArrowUp, ArrowRight, Globe2,
} from 'lucide-react';


const EMPTY_STATS = {
  overview: {
    total_payments: 0,
    approved_payments: 0,
    pending_payments: 0,
    payment_completion_rate: 0,
    churn_rate: 0,
    retention_rate: 0
  },
  financial: {
    total_revenue: 0,
    pending_revenue: 0,
    avg_ticket: 0,
    processed_sales: 0,
    pending_sales: 0
  },
  time_metrics: {
    avg_return_time: 0,
    avg_conversion_time: 0
  },
  users: {
    total: 0,
    active: 0,
    blocked: 0,
    paying: 0,
    pending: 0,
    recurrent: 0,
    codigo_venda_percentage: 0
  },
  plan_types: {
    normal: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 },
    downsell: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 },
    orderbump: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 },
    disparo: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 },
    upsell: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 },
    flow: { approved: 0, pending: 0, revenue: 0, conversion_rate: 0 }
  },
  hourly: {
    labels: Array.from({ length: 24 }, (_, hour) => `${hour}h`),
    views: Array(24).fill(0),
    pending_purchases: Array(24).fill(0),
    purchases: Array(24).fill(0)
  },
  top_plans: [],
  top_weekdays: [],
  top_hours: []
};


export default function Statistics() {

  const { user } = useAuth();

  const [data, setData] = useState(EMPTY_STATS);
  const [sectionsLoaded, setSectionsLoaded] = useState({
    overview: false,
    financial: false,
    users: false,
    hourly: false,
    plans: false,
    rankings: false
  });

  // Filtros aplicados — a seção Países busca sozinha quando entra na tela
  const [filtersBody, setFiltersBody] = useState(null);

  const visibleBots = getVisibleBots(user?.bots);

  function handleApplyFilters(filters) {
    setData(EMPTY_STATS);
    setSectionsLoaded({
      overview: false,
      financial: false,
      users: false,
      hourly: false,
      plans: false,
      rankings: false
    });

    const body = {
      start_date: filters.startDate,
      end_date: filters.endDate,
    };

    if (filters.botIds && filters.botIds.length > 0) body.bot_ids = filters.botIds;
    else if (filters.botId) body.bot_id = filters.botId;

    setFiltersBody(body);

    const loadSection = (sectionKey, requestPromise) => {
      requestPromise
        .then(res => {
          if (res.data.success) {
            setData(prev => ({ ...prev, ...res.data.data }));
          } else {
            console.error(`Erro ao carregar ${sectionKey}:`, res.data.error || 'Erro ao carregar dados');
          }
        })
        .catch(err => {
          console.error(`Erro ao carregar ${sectionKey}:`, err);
        })
        .finally(() => {
          setSectionsLoaded(prev => ({ ...prev, [sectionKey]: true }));
        });
    };

    statisticsService.getSummary(body)
      .then(res => {
        if (res.data.success) {
          setData(prev => ({ ...prev, ...res.data.data }));
        } else {
          console.error('Erro ao carregar resumo:', res.data.error || 'Erro ao carregar dados');
        }
      })
      .catch(err => {
        console.error('Erro ao carregar resumo:', err);
      })
      .finally(() => {
        setSectionsLoaded(prev => ({ ...prev, overview: true, financial: true }));
      });

    loadSection('users', statisticsService.getUsers(body));
    loadSection('hourly', statisticsService.getHourly(body));
    loadSection('plans', statisticsService.getPlans(body));
    loadSection('rankings', statisticsService.getRankings(body));
  }

  const { overview, financial, users, time_metrics, plan_types, top_plans, top_weekdays, top_hours, hourly } = data;








  return (
    <main className="page">
      <div className="page-header-with-filters">
        <div className="page-header-content">
          <h1 className="title">
            <LineChartIcon size={24} strokeWidth={2.25} /> Estatísticas
          </h1>
          <p>Análise completa de desempenho</p>
        </div>
      </div>

      <FiltersSection
        onApplyFilters={handleApplyFilters}
        gateway={user?.gateways || []}
        bots={visibleBots}
        showGatewayFilter={false}
        enableBotMultiSelect={true}
      />


      {/* Visão Geral */}
      <h2 style={{ color: '#fff', margin: '30px 0 20px 0', fontSize: '20px' }}>
        <BarChart3 size={18} strokeWidth={2.25} /> Visão Geral
      </h2>
      <div className="stats-grid">
        {!sectionsLoaded.overview ? (
          <>{Array(4).fill(0).map((_, i) => <SkeletonCard key={i} height="188px" />)} </>) : (
          <>

            <StatCardStatistics
              icon={{ Icon: Banknote, color: 'green' }}
              title="Receita Total"
              value={fmtCurrencyBR.format(financial.total_revenue)}
              subtitle={`${financial.processed_sales} vendas aprovadas`}
            />
            <StatCardStatistics
              icon={{ Icon: Hourglass, color: 'orange' }}
              title="Receita Pendente"
              value={fmtCurrencyBR.format(financial.pending_revenue)}
              subtitle={`${financial.pending_sales} vendas pendentes`}
            />

            <StatCardPercentage
              icon={{ Icon: CheckCircle, color: 'green' }}
              title="Vendas Aprovadas"
              value={`${fmtNumberBR.format(overview.approved_payments)} vendas`}
              percentage={overview.payment_completion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: RefreshCw, color: 'purple' }}
              title="Retenção"
              value={`${overview.retention_rate}%`}
              percentage={overview.retention_rate}
            />
          </>
        )}
      </div>


      {/* Gráficos por Horário */}
      <div className='mb-4'>
        {!sectionsLoaded.hourly || !hourly ? <SkeletonChart /> : (
          <div className="statistics-charts-section">
            {/* Gráfico Combinado com 2 linhas */}
            <LineChart
              title="Horários de Pico (24h)"
              labels={hourly.labels}
              datasets={[
                {
                  data: hourly.views,
                  label: "Starts",
                  color: "#dcdcdc"
                },
                {
                  data: hourly.pending_purchases,
                  label: "Gerados",
                  color: "#f9a916"
                },
                {
                  data: hourly.purchases,
                  label: "Pagos",
                  color: "#00ff1e"
                },

              ]}
            />

          </div>
        )}
      </div>

      {/* Financeiro */}
      {/* <h2 style={{ color: '#fff', margin: '30px 0 20px 0', fontSize: '20px' }}>
        <i className="fas fa-dollar-sign"></i> Financeiro
      </h2> */}
      <div className="stats-grid">
        {!sectionsLoaded.financial ? (
          <>{Array(4).fill(0).map((_, i) => <SkeletonCard key={i} height="158px" />)}</>
        ) : (
          <>
            <StatCardStatistics
              icon={{ Icon: PieChart, color: 'blue' }}
              title="Ticket Médio"
              value={fmtCurrencyBR.format(financial.avg_ticket)}
              subtitle="Por venda aprovada"
            />

            <StatCardStatistics
              icon={{ Icon: Clock, color: 'purple' }}
              title="Tempo de Conversão"
              value={`${(Number(time_metrics?.avg_conversion_time) || 0).toFixed(1)}h`}
              subtitle="Do /start ao pagamento"
            />
            {/* Usa dados de /users (outra request): skeleton até chegar, senão
                aparece "0 de 0" e depois pula para o valor real */}
            {!sectionsLoaded.users ? <SkeletonCard height="158px" /> : (
              <StatCardPercentage
                icon={{ Icon: ArrowRightLeft, color: 'green' }}
                title="Conversão Start → Pago"
                value={`${fmtNumberBR.format(users.paying)} pagantes`}
                subtitle={`de ${fmtNumberBR.format(users.total)} usuários`}
                percentage={users.total > 0 ? parseFloat((users.paying / users.total * 100).toFixed(2)) : 0}
              />
            )}
            <StatCardStatistics
              icon={{ Icon: Repeat, color: 'blue' }}
              title="Tempo de Retorno"
              value={`${(Number(time_metrics?.avg_return_time) || 0).toFixed(1)}h`}
              subtitle="Usuários recorrentes"
            />
          </>
        )}
      </div>

      {/* Usuários */}
      <h2 style={{ color: '#fff', margin: '30px 0 20px 0', fontSize: '20px' }}>
        <Users size={18} strokeWidth={2.25} /> Usuários
      </h2>
      <div className="stats-grid">
        {!sectionsLoaded.users ? (
          <>{Array(5).fill(0).map((_, i) => <SkeletonCard key={i} height="158px" />)}</>
        ) : (
          <>
            <StatCardStatistics
              icon={{ Icon: Users, color: 'blue' }}
              title="Total de Usuários"
              value={fmtNumberBR.format(users.total)}
              subtitle={`${users.active} ativos`}
            />
            <StatCardStatistics
              icon={{ Icon: DollarSign, color: 'green' }}
              title="Usuários Pagantes"
              value={fmtNumberBR.format(users.paying)}
              subtitle={`${users.recurrent} recorrentes`}
            />
            <StatCardStatistics
              icon={{ Icon: PauseCircle, color: 'orange' }}
              title="Usuários Pendentes"
              value={fmtNumberBR.format(users.pending)}
              subtitle={`Com pagamento pendente`}
            />
            <StatCardStatistics
              icon={{ Icon: Ban, color: 'red' }}
              title="Usuários Bloqueados"
              value={fmtNumberBR.format(users.blocked)}
              subtitle={`Bloquaram o Bot`}
            />
            <StatCardPercentage
              icon={{ Icon: Barcode, color: 'purple' }}
              title="Código de Venda"
              value={`${users.codigo_venda_percentage}%`}
              percentage={users.codigo_venda_percentage}
            />
          </>
        )}
      </div>

      {/* Tipos de Planos */}
      <h2 style={{ color: '#fff', margin: '30px 0 20px 0', fontSize: '20px' }}>
        <Tags size={18} strokeWidth={2.25} /> Tipos de Planos
      </h2>
      <div className="stats-grid">
        {!sectionsLoaded.plans ? (
          <>{Array(6).fill(0).map((_, i) => <SkeletonCard key={i} height="158px" />)}</>
        ) : (
          <>
            <StatCardPercentage
              icon={{ Icon: Box, color: 'blue' }}
              title="Planos Normais"
              value={fmtCurrencyBR.format(plan_types.normal.revenue)}
              subtitle={`${plan_types.normal.approved}/${plan_types.normal.pending + plan_types.normal.approved} PIX`}
              percentage={plan_types.normal.conversion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: ArrowDown, color: 'orange' }}
              title="Downsells"
              value={fmtCurrencyBR.format(plan_types.downsell.revenue)}
              subtitle={`${plan_types.downsell.approved}/${plan_types.downsell.pending + plan_types.downsell.approved} PIX`}
              percentage={plan_types.downsell.conversion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: Gift, color: 'green' }}
              title="Order Bumps"
              value={fmtCurrencyBR.format(plan_types.orderbump.revenue)}
              subtitle={`${plan_types.orderbump.approved}/${plan_types.orderbump.pending + plan_types.orderbump.approved} PIX`}
              percentage={plan_types.orderbump.conversion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: Send, color: 'purple' }}
              title="Disparos"
              value={fmtCurrencyBR.format(plan_types.disparo.revenue)}
              subtitle={`${plan_types.disparo.approved}/${plan_types.disparo.pending + plan_types.disparo.approved} PIX`}
              percentage={plan_types.disparo.conversion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: ArrowUp, color: 'blue' }}
              title="Upsell"
              value={fmtCurrencyBR.format(plan_types.upsell.revenue)}
              subtitle={`${plan_types.upsell.approved}/${plan_types.upsell.pending + plan_types.upsell.approved} PIX`}
              percentage={plan_types.upsell.conversion_rate}
            />
            <StatCardPercentage
              icon={{ Icon: ArrowRight, color: 'orange' }}
              title="Flow"
              value={fmtCurrencyBR.format(plan_types.flow.revenue)}
              subtitle={`${plan_types.flow.approved}/${plan_types.flow.pending + plan_types.flow.approved} PIX`}
              percentage={plan_types.flow.conversion_rate}
            />
          </>
        )}
      </div>



      {/* Rankings */}
      <div className="rankings-section">
        {!sectionsLoaded.rankings ? (
          <Loading text="Carregando rankings..." style={{ textAlign: 'center', padding: '40px' }} />
        ) : (
          <>
            <div className="ranking-card">
              <h3><Crown size={16} strokeWidth={2.25} /> Top 3 Planos</h3>
              {top_plans.length > 0 ? (
                top_plans.map((plan, idx) => (
                  <RankingItem
                    key={idx}
                    position={idx + 1}
                    name={plan.name}
                    count={plan.count}
                    revenue={plan.revenue}
                  />
                ))
              ) : (
                <p>Sem dados</p>
              )}
            </div>

            <div className="ranking-card">
              <h3><Calendar size={16} strokeWidth={2.25} /> Top 3 Dias da Semana</h3>
              {top_weekdays.length > 0 ? (
                top_weekdays.map((day, idx) => (
                  <RankingItem
                    key={idx}
                    position={idx + 1}
                    name={day.weekday}
                    count={day.count}
                    revenue={day.revenue}
                  />
                ))
              ) : (
                <p>Sem dados</p>
              )}
            </div>

            <div className="ranking-card">
              <h3><Clock size={16} strokeWidth={2.25} /> Top 3 Horários</h3>
              {top_hours.length > 0 ? (
                top_hours.map((hour, idx) => (
                  <RankingItem
                    key={idx}
                    position={idx + 1}
                    name={hour.hour}
                    count={hour.count}
                    revenue={hour.revenue}
                  />
                ))
              ) : (
                <p>Sem dados</p>
              )}
            </div>
          </>
        )}
      </div>

      {/* Países */}
      <h2 style={{ color: '#fff', margin: '30px 0 20px 0', fontSize: '20px' }}>
        <Globe2 size={18} strokeWidth={2.25} /> Países
      </h2>
      <CountriesSection filtersBody={filtersBody} />
    </main>
  );
}